'use client';
import { useEffect } from 'react';
import { TKeyValuePair, IPagination } from '@/types';
import * as Styled from './styled';

import { usePathname, useSearchParams } from 'next/navigation';

import { Row } from '@/ui/primatives';
import * as Icon from '@/ui/Icon';
import Button from '@/ui/Button';

import KeyEditorToolTip from '@/components/CompareTabular/KeyEditor/KeyEditorToolTip';
import DownloadResults from './DownloadResults';

import Link from 'next/link';
import useDataframeContext from '../DataframeContext';
import useDataframeLayoutContext from '../DataFrameLayoutContext';
import useDataframeQueryContext from '../DataframeQueryContext';
import { FullscreenToggle } from '@/ui/FullscreenExpando';

import { DIFF_STATUS_FIELD_NAME } from '@/components/DataFrame/consts';

const TotalRowCount: React.FC<{ count?: number; selected: boolean; targetUrl: string }> = ({
  count,
  selected,
  targetUrl,
}) => {
  if (!count) return null;
  return (
    <Styled.RowCountIndicator $selected={selected} className="rows-total">
      <Link href={targetUrl}>{count} total rows</Link>
    </Styled.RowCountIndicator>
  );
};

const AddedRowCount: React.FC<{ count?: number; selected: boolean; targetUrl: string }> = ({
  count,
  selected,
  targetUrl,
}) => {
  if (!count) return null;
  return (
    <Styled.RowCountIndicator $selected={selected} className="rows-added">
      <Link href={targetUrl}>
        <Icon.Add size="12" /> {count}
      </Link>
    </Styled.RowCountIndicator>
  );
};

const RemovedRowCount: React.FC<{ count?: number; selected: boolean; targetUrl: string }> = ({
  count,
  selected,
  targetUrl,
}) => {
  if (!count) return null;
  return (
    <Styled.RowCountIndicator $selected={selected} className="rows-removed">
      <Link href={targetUrl}>
        <Icon.Minus size="12" /> {count}
      </Link>
    </Styled.RowCountIndicator>
  );
};

const ModifiedRowCount: React.FC<{ count?: number; selected: boolean; targetUrl: string }> = ({
  count,
  selected,
  targetUrl,
}) => {
  if (!count) return null;
  return (
    <Styled.RowCountIndicator $selected={selected} className="rows-modified">
      <Link href={targetUrl}>
        <Icon.Triangle size="12" /> {count}
      </Link>
    </Styled.RowCountIndicator>
  );
};

const PaginatedViewMetrics: React.FC<{ pagination: IPagination; viewData: any; activeFilterName?: string }> = ({
  pagination,
  viewData,
  activeFilterName,
}) => {
  const totalEntries = pagination?.total_entries;
  const visibleEntries = viewData.data.length;
  const columnCount = viewData.schema.fields.filter(
    (field: TKeyValuePair) => field.name !== DIFF_STATUS_FIELD_NAME,
  ).length;

  const startEntry = (pagination.page_number - 1) * pagination.page_size + 1;
  const endEntry = startEntry + visibleEntries - 1;
  return (
    <div>
      {columnCount} columns,
      {visibleEntries === totalEntries ? '' : ` ${startEntry}-${endEntry} of`} {totalEntries} {activeFilterName} rows
    </div>
  );
};

const getActiveFilter = (searchParams: URLSearchParams): { filter: TKeyValuePair; name: string | undefined } => {
  const appliedFilter = searchParams.get('filter');
  const filter = {
    added: `${DIFF_STATUS_FIELD_NAME}==added`,
    removed: `${DIFF_STATUS_FIELD_NAME}==removed`,
    modified: `${DIFF_STATUS_FIELD_NAME}==modified`,
  };
  const activeFilter: TKeyValuePair = {
    added: appliedFilter == filter.added,
    removed: appliedFilter == filter.removed,
    modified: appliedFilter == filter.modified,
  };

  const name = Object.keys(activeFilter).find((key) => activeFilter[key]);

  return { filter: activeFilter, name };
};

const ControlHeaderRow: React.FC<{}> = ({}) => {
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const context = useDataframeContext();
  const { viewData, sourceData, pagination, compareResponse, rows, addBlankRow, editable } = context;

  const {
    setDetailsPaneVisible,
    setSchemaPaneVisible,
    leftSidebarWidth,
    rightSidebarWidth,
    schemaPaneVisible,
    detailsPaneVisible,
  } = useDataframeLayoutContext();

  const { setNextQuery } = useDataframeQueryContext();

  useEffect(() => {
    const queryObject = Object.fromEntries(searchParams.entries());
    setNextQuery(queryObject);
  }, [searchParams, setNextQuery]);

  if (!viewData) return <div>Updating...</div>;

  const compareSummary = compareResponse?.summary;

  const isCompare = !!compareSummary;
  const { added_rows, removed_rows, modified_rows } = compareSummary?.modifications || {};

  const filter = {
    added: `${DIFF_STATUS_FIELD_NAME}==added`,
    removed: `${DIFF_STATUS_FIELD_NAME}==removed`,
    modified: `${DIFF_STATUS_FIELD_NAME}==modified`,
  };

  const allRowsUrl = `${pathName}`;
  const addedRowsUrl = `${pathName}?filter=${filter.added}`;
  const removedRowsUrl = `${pathName}?filter=${filter.removed}`;
  const modifiedRowsUrl = `${pathName}?filter=${filter.modified}`;

  const activeFilter = getActiveFilter(searchParams);
  const viewRowCount = viewData.pagination.total_entries;
  const sourceRowCount = sourceData.size.height;
  const isFiltered = sourceRowCount > viewRowCount;

  return (
    <Styled.ControlHeaderRow>
      <Styled.LeftPane style={{ width: leftSidebarWidth }}>
        {schemaPaneVisible ? (
          <Row alignItems="center" justifyContent="space-between" gap={20}>
            <Row alignItems="center" gap={5}>
              <div>Schema</div>
              {isCompare && <KeyEditorToolTip />}
            </Row>
            <Icon.SidebarLeft onClick={() => setSchemaPaneVisible(!schemaPaneVisible)} />
          </Row>
        ) : (
          <Icon.SidebarRight onClick={() => setSchemaPaneVisible(!schemaPaneVisible)} />
        )}
      </Styled.LeftPane>
      <Styled.ContentPane $leftPaneExpanded={schemaPaneVisible}>
        <PaginatedViewMetrics pagination={pagination} viewData={viewData} activeFilterName={activeFilter.name} />
        {isCompare && (
          <Row gap={10} alignItems="center">
            <TotalRowCount
              count={added_rows + removed_rows + modified_rows}
              selected={!activeFilter}
              targetUrl={allRowsUrl}
            />
            <AddedRowCount count={added_rows} selected={activeFilter.filter.added} targetUrl={addedRowsUrl} />
            <RemovedRowCount count={removed_rows} selected={activeFilter.filter.removed} targetUrl={removedRowsUrl} />
            <ModifiedRowCount
              count={modified_rows}
              selected={activeFilter.filter.modified}
              targetUrl={modifiedRowsUrl}
            />
          </Row>
        )}
        {editable && (
          <Row alignItems="center" gap={5}>
            <DownloadResults />
            <Button.Basic tiny onClick={addBlankRow}>
              <Icon.Add size="12" /> Add Row
            </Button.Basic>
            <FullscreenToggle iconSize={16} margin={0} />
          </Row>
        )}
        {isFiltered && !isCompare && <div className="hide-mobile">Filtered from {sourceRowCount} rows</div>}
      </Styled.ContentPane>
      <Styled.RightPane $expanded={detailsPaneVisible} style={{ width: rightSidebarWidth }}>
        {detailsPaneVisible ? (
          <Row alignItems="center" justifyContent="space-between">
            <Icon.SidebarRight onClick={() => setDetailsPaneVisible(!detailsPaneVisible)} />
            <Row gap={10} alignItems="center">
              <div>Row details</div>
            </Row>
          </Row>
        ) : (
          <Icon.SidebarLeft onClick={() => setDetailsPaneVisible(!detailsPaneVisible)} />
        )}
      </Styled.RightPane>
    </Styled.ControlHeaderRow>
  );
};

export default ControlHeaderRow;
